import { watch } from "vue";
import { createRouter, createWebHistory } from "vue-router";
import {
    fullScreenLoader,
    removeFullScreenLoader,
} from "@/components/jsComponents";
import { skapi } from "@/main";
import { user } from "@/code/user";

import Main from "@/views/main.vue";
import ServiceMain from "@/views/service/main.vue";
import LandingPage from "@/views/landing.vue";
import Login from "@/views/login.vue";
import Signup from "@/views/signup.vue";
import NotFound from "@/views/notFound.vue";

let checkUser = async (t, f, n) => {
    let u = user?.user_id;
    if (u) {
        return n();
    } else {
        // subscription 페이지로 바로 접근 했는데 로그인이 안된 상태라면 로그인 페이지로 보내고, 로그인 후에 subscription 페이지로 돌아오게 함
        if (t.name === "subscription" || t.name === "subscription-url") {
            let redirectPath = t.name ? t.name : "";
            let serviceId = t.params.service ? t.params.service : "";
            n(`/login?redirect=true&rdp=${redirectPath}&sid=${serviceId}`);
            return;
        }
    }
    n("/login");
};

const router = createRouter({
    scrollBehavior(to, from, savedPosition) {
        // always scroll to top
        return { top: 0 };
    },
    history: createWebHistory(import.meta.env.BASE_URL),
    routes: [
        {
            path: "/login",
            name: "login",
            component: Login,
            beforeEnter: async (to, from, next) => {
                if (user.user_id) {
                    // my-services 페이지로 리다이렉트
                    next({ name: "myservices" });
                    return;
                }
                next();
            },
        },
        {
            path: "/signup",
            name: "signup",
            component: Signup,
        },
        {
            path: "/confirmation",
            name: "confirmation",
            component: () => import("@/views/confirmation.vue"),
        },
        {
            path: "/verification",
            name: "verification",
            component: () => import("@/views/verification.vue"),
        },
        {
            path: "/forgot",
            name: "forgot",
            component: () => import("@/views/forgot-password.vue"),
        },
        {
            path: "/change-password",
            name: "change-password",
            component: () => import("@/views/change-password.vue"),
            beforeEnter: checkUser,
        },
        {
            path: "/delete-account",
            name: "delete-account",
            component: () => import("@/views/delete-account.vue"),
            beforeEnter: checkUser,
        },
        {
            path: "/signup_confirmed_template",
            name: "signup_confirmed",
            component: () => import("@/views/signup_confirmed_template.vue"),
        },
        {
            path: "/invitation_confirmed_template",
            name: "invitation_confirmed",
            component: () =>
                import("@/views/invitation_confirmed_template.vue"),
        },
        {
            path: "/enable-account/:email",
            name: "enable-account",
            component: () => import("@/views/enable-account.vue"),
        },
        {
            path: "/success/:how",
            name: "success",
            component: () => import("@/views/success.vue"),
        },
        {
            path: "/success",
            name: "success-default",
            component: () => import("@/views/success.vue"),
        },
        {
            path: "/bye",
            name: "bye",
            component: () => import("@/views/bye.vue"),
        },
        {
            path: "/refer/:name",
            name: "refer",
            component: () => import("@/views/refer.vue"),
        },
        {
            // path: '/create/:name',
            path: "/create",
            name: "create",
            component: () => import("@/views/create-service.vue"),
            beforeEnter: checkUser,
        },
        {
            path: "/delete-service/:id",
            name: "delete-service",
            component: () => import("@/views/delete-service.vue"),
            beforeEnter: checkUser,
        },
        {
            path: "/subscription/:service",
            name: "subscription",
            component: () => import("@/views/subscription/subscription.vue"),
            beforeEnter: checkUser,
        },
        {
            path: "/subscription/url/:service",
            name: "subscription-url",
            component: () => import("@/views/subscription/url.vue"),
            beforeEnter: checkUser,
        },
        {
            path: "/test-page",
            name: "test-page",
            component: () => import("@/views/test-page.vue"),
        },
        {
            path: "/",
            component: Main,
            children: [
                {
                    path: "/",
                    name: "home",
                    component: LandingPage,
                },
                {
                    path: "use-cases",
                    name: "usecases",
                    component: () => import("@/views/use-cases.vue"),
                },
                {
                    path: "account-setting",
                    name: "accountsetting",
                    component: () => import("@/views/account-setting.vue"),
                    beforeEnter: checkUser,
                },
                {
                    path: "my-services",
                    name: "myservices",
                    component: () => import("@/views/service-list.vue"),
                    beforeEnter: checkUser,
                },
                {
                    path: "my-services/:service",
                    // component: () => import("@/views/service/main.vue"),
                    component: ServiceMain,
                    beforeEnter: checkUser,
                    children: [
                        {
                            path: "",
                            name: "service",
                            component: () =>
                                import("@/views/service/getting_started.vue"),
                        },
                        {
                            path: "dashboard",
                            name: "dashboard",
                            component: () =>
                                import("@/views/service/service.vue"),
                        },
                        {
                            path: "users",
                            name: "users",
                            component: () =>
                                import("@/views/service/users/users.vue"),
                        },
                        {
                            path: "records",
                            name: "records",
                            component: () =>
                                import("@/views/service/records/records.vue"),
                        },
                        {
                            path: "openid",
                            name: "openid",
                            component: () =>
                                import("@/views/service/openid/openid.vue"),
                        },
                        {
                            path: "mail",
                            name: "mail",
                            component: () => import("@/views/service/mail.vue"),
                        },
                        {
                            path: "newsletter",
                            name: "newsletter",
                            component: () =>
                                import("@/views/service/newsletter.vue"),
                        },
                        {
                            path: "hosting",
                            name: "hosting",
                            component: () =>
                                import("@/views/service/hosting/hosting.vue"),
                        },
                        {
                            path: "clientsecret",
                            name: "clientsecret",
                            component: () =>
                                import("@/views/service/client_secret.vue"),
                        },
                        {
                            path: "etc/testing",
                            name: "etc-testing",
                            component: () =>
                                import("@/views/service/testing.vue"),
                        },
                        {
                            path: "test/openid",
                            name: "test/openid",
                            component: () =>
                                import("@/views/service/tester/openid.vue"),
                        },
                    ],
                },
            ],
        },

        // test pages
        {
            path: "/test/ui",
            name: "ui",
            component: () => import("@/views/ui.vue"),
        },

        {
            path: "/test/playground",
            name: "playground",
            component: () => import("@/views/playground.vue"),
        },

        // 404 page
        {
            path: "/404page",
            name: "404page",
            component: NotFound,
        },
        {
            path: "/:pathMatch(.*)*",
            redirect: "/404page",
        },
    ],
});

// Declare gtag as a global variable
declare const gtag: (...args: any[]) => void;

// 깜빡임 방지용 타이머
let loadingTimer: number | null = null;

router.beforeEach(async (to, from, next) => {
    // 너무 짧은 이동은 로딩 안 보이게 150ms 지연
    loadingTimer = window.setTimeout(() => {
        fullScreenLoader();
    }, 150);

    next();
});

// 라우트 변경 시 GA 이벤트 전송
router.afterEach((to) => {
    if (typeof gtag !== "undefined") {
        gtag("config", import.meta.env.VITE_GTAG, {
            page_path: to.fullPath,
        });
    }

    if (loadingTimer) {
        clearTimeout(loadingTimer);
        loadingTimer = null;
    }

    // Google OAuth 처리 중이 아닐 때만 로더 제거
    const urlParams = new URLSearchParams(window.location.search);
    if (!urlParams.get("code")) {
        removeFullScreenLoader();
    }
});

// 에러 발생 시에도 로딩 해제
router.onError(() => {
    if (loadingTimer) {
        clearTimeout(loadingTimer);
        loadingTimer = null;
    }

    // Google OAuth 처리 중이 아닐 때만 로더 제거
    const urlParams = new URLSearchParams(window.location.search);
    if (!urlParams.get("code")) {
        removeFullScreenLoader();
    }
});

// pendServiceFetchError watch 설정 (동적 import로 순환 참조 방지)
(async () => {
    const { pendServiceFetchError, loadServiceList } =
        await import("@/views/service-list");

    watch(pendServiceFetchError, async (newVal) => {
        if (newVal) {
            // 현재 라우트에 params.service 있는지 확인
            const currentRoute = router.currentRoute?.value;
            const serviceId = (currentRoute?.params?.service as string) || "";
            if (serviceId && serviceId.startsWith("pend-")) {
                // my-services 페이지로 리다이렉트
                await router.replace({ name: "myservices" });
                pendServiceFetchError.value = false;
                // 에러난 서비스를 제외하고 나머지 서비스 다시 로드
                await loadServiceList();
            }
        }
    });
})();

export default router;
